import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchScreenOrders } from '../../services/screenApi';
import useScreenAuth from '../../components/useScreenAuth';
import { clearScreenAuthData } from '../../utils/screenAuthStorage';
import '../../css/screenview.css';
import '../../css/ordersGlovoKooul.css';
import kooulPendingAudioSrc from '../../sounds/mp3/bip_order_kooul.mp3';
import axios from 'axios';
import { header } from '../../components/header.encry';
import { getSessionScreenAuth } from '../../utils/screenAuthStorage';
import Modal from '../../components/Modal';

const ScreenOrdersView = () => {
  const [orders, setOrders] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: isAuthLoading, screen_code } = useScreenAuth();
  const intervalRef = useRef(null);
  const kooulPendingAudioRef = useRef(null);
  const imageModalRef = useRef(null);
  const modalTimeoutRef = useRef(null);
  const orderLinkColorsRef = useRef(new Map());
  const orderLinkColorIndexRef = useRef(0);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [imageZoom, setImageZoom] = useState(1);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [storeNames, setStoreNames] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [storeUsers, setStoreUsers] = useState({});
  const [usersList, setUsersList] = useState([]);
  const [columnUserIds, setColumnUserIds] = useState(() => Array(4).fill(null));
  const [columnOrderIndices, setColumnOrderIndices] = useState(() => Array(4).fill(0));
  const NUM_COLUMNS = 4;
  const [userColumn, setUserColumn] = useState(1); // 1..NUM_COLUMNS
  const API_URL = process.env?.REACT_APP_PEGASUS_API_URL;

  const getColumnStorageKey = () => `screenColumn:${screen_code || 'default'}`;

  const getColumnUsersKey = () => `screenColumnUsers:${screen_code || 'default'}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(getColumnStorageKey()) || sessionStorage.getItem('screenUserColumn');
      const parsed = parseInt(raw, 10);
      if (Number.isFinite(parsed) && parsed >= 1 && parsed <= NUM_COLUMNS) {
        setUserColumn(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, [screen_code]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(getColumnUsersKey());
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setColumnUserIds((old) => {
            const out = Array(NUM_COLUMNS).fill(null);
            for (let i = 0; i < NUM_COLUMNS; i++) {
              out[i] = parsed[i] || null;
            }
            return out;
          });
          return;
        }
      }
    } catch (e) {
      // ignore
    }
    // default will be assigned later when usersList is loaded
  }, [screen_code]);

  const persistUserColumn = (col) => {
    const clamped = Math.max(1, Math.min(NUM_COLUMNS, Number(col) || 1));
    setUserColumn(clamped);
    try {
      sessionStorage.setItem(getColumnStorageKey(), String(clamped));
      sessionStorage.setItem('screenUserColumn', String(clamped)); // fallback
    } catch (e) {
      // ignore
    }
  };

  const prevColumn = () => persistUserColumn(userColumn === 1 ? NUM_COLUMNS : userColumn - 1);
  const nextColumn = () => persistUserColumn(userColumn === NUM_COLUMNS ? 1 : userColumn + 1);

  const persistColumnUserIds = (arr) => {
    try {
      sessionStorage.setItem(getColumnUsersKey(), JSON.stringify(arr));
    } catch (e) {}
  };

  const prevUserForColumn = (colIdx) => {
    setColumnUserIds((prev) => {
      const out = [...prev];
      // Swap with previous column (or last column if first)
      const targetIdx = colIdx === 0 ? NUM_COLUMNS - 1 : colIdx - 1;
      const temp = out[colIdx];
      out[colIdx] = out[targetIdx];
      out[targetIdx] = temp;
      persistColumnUserIds(out);
      return out;
    });
  };

  const nextUserForColumn = (colIdx) => {
    setColumnUserIds((prev) => {
      const out = [...prev];
      // Swap with next column (or first column if last)
      const targetIdx = colIdx === NUM_COLUMNS - 1 ? 0 : colIdx + 1;
      const temp = out[colIdx];
      out[colIdx] = out[targetIdx];
      out[targetIdx] = temp;
      persistColumnUserIds(out);
      return out;
    });
  };

  const prevOrderForColumn = (colIdx, ordersForUser) => {
    setColumnOrderIndices((prev) => {
      const out = [...prev];
      const count = (ordersForUser && ordersForUser.length) || 0;
      if (count === 0) {
        out[colIdx] = 0;
        return out;
      }
      const cur = Number.isFinite(out[colIdx]) ? out[colIdx] : 0;
      out[colIdx] = cur <= 0 ? count - 1 : cur - 1;
      return out;
    });
  };

  const nextOrderForColumn = (colIdx, ordersForUser) => {
    setColumnOrderIndices((prev) => {
      const out = [...prev];
      const count = (ordersForUser && ordersForUser.length) || 0;
      if (count === 0) {
        out[colIdx] = 0;
        return out;
      }
      const cur = Number.isFinite(out[colIdx]) ? out[colIdx] : 0;
      out[colIdx] = cur >= count - 1 ? 0 : cur + 1;
      return out;
    });
  };

  const requestFullscreen = () => {
    const root = document.documentElement;
    if (!root) return;

    if (!document.fullscreenElement) {
      if (root.requestFullscreen) {
        root.requestFullscreen();
      } else if (root.webkitRequestFullscreen) {
        root.webkitRequestFullscreen();
      }
    }
  };

  const handleDisconnect = () => {
    try {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (e) {}
    clearScreenAuthData();
    navigate('/screens/login');
  };

  const resolveImageUrl = (path = '') => {
    if (!path) {
      return '';
    }
    if (path.startsWith('http')) return path;
    const base = process.env?.REACT_APP_DATA_PIC_PATH;
    return base ? `${base}/${path}` : path;
  };

  const cleanupImageModalArtifacts = () => {
    const modalElement = document.getElementById('showModal');
    if (modalElement) {
      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      modalElement.setAttribute('aria-hidden', 'true');
      modalElement.removeAttribute('aria-modal');
      modalElement.removeAttribute('role');
    }

    if (!document.querySelector('.modal.show')) {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
    }
  };

  const closeImageModal = () => {
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }

    const modalElement = document.getElementById('showModal');
    try {
      const instance = modalElement && window.bootstrap
        ? window.bootstrap.Modal.getInstance(modalElement)
        : imageModalRef.current;
      if (instance) {
        instance.hide();
      }
    } catch (e) {
      // fallback cleanup below
    }

    setShowModal(false);
    window.setTimeout(cleanupImageModalArtifacts, 350);
  };

  const handleImageModalHidden = () => {
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }
    setShowModal(false);
    cleanupImageModalArtifacts();
  };

  const openImage = (url) => {
    const resolvedUrl = resolveImageUrl(url);
    if (!resolvedUrl) return;
    
    // Clear any existing timeout
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
    }
    
    // Update state with new image
    setModalImage(resolvedUrl);
    setImageZoom(1);
    setShowModal(true);
    
    // Wait for React to update DOM with new image, then show modal
    setTimeout(() => {
      const modalElement = document.getElementById('showModal');
      if (modalElement) {
        imageModalRef.current = window.bootstrap.Modal.getOrCreateInstance(modalElement);
        imageModalRef.current.show();
        
        // Auto-close after 5 minutes (300000ms)
        modalTimeoutRef.current = setTimeout(() => {
          closeImageModal();
        }, 300000);
      }
    }, 50);
  };

  const loadOrders = async () => {
    if (!token) {
      return;
    }
    try {
      const data = await fetchScreenOrders(token);
      const normalized = Array.isArray(data) ? data : data?.orders || data?.response || [];
      setOrders([...(normalized || [])]);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      if (error?.response?.status === 401) {
        clearScreenAuthData();
        navigate('/screens/login');
      } else {
        toast.error('Erreur lors du chargement des commandes');
        console.error('Unable to fetch screen orders', error);
      }
    }
  };

  const normalizeProductComponents = (rawComponents) => {
    if (!Array.isArray(rawComponents)) return [];
    return rawComponents.map((item) => {
      const rawReference = item?.product ?? item?.component;
      const name = item?.name ?? (typeof rawReference === 'object' ? rawReference?.name : undefined) ?? '';
      const quantityValue = Number(item?.quantity ?? 0);
      return {
        ...item,
        product: item?.product ?? rawReference,
        name,
        quantity: Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 0,
      };
    });
  };

  const loadComponents = async (productId) => {
    if (!productId) return;
    try {
      const response = await axios.get(`${process.env?.REACT_APP_PEGASUS_API_URL}/product/${productId}`, header());
      const product = response.data;
      if (product) {
        const normalizedComponents = normalizeProductComponents(product.components);
        setCurrentProduct({ ...product, components: normalizedComponents });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des composants');
      console.error('Unable to load product components', error);
    }
  };

  const endOrder = async (orderCode) => {
    try {
      const response = await axios.post(`${process.env?.REACT_APP_PEGASUS_API_URL}/endOrder`, { orderCode }, header());
      if (response.status === 200) loadOrders();
      else toast.error('Erreur lors de la fermeture de la commande');
    } catch (error) {
      toast.error('Erreur lors de la fermeture de la commande');
      console.error('endOrder error', error);
    }
  };

  useEffect(() => {
    const audio = new Audio(kooulPendingAudioSrc);
    audio.loop = true;
    kooulPendingAudioRef.current = audio;

    return () => {
      audio.pause();
      audio.currentTime = 0;
      kooulPendingAudioRef.current = null;
    };
  }, []);

  const hasKooulPendingOrders = useMemo(() => {
    if (!Array.isArray(orders) || orders.length === 0) {
      return false;
    }

    return orders.some((order) => {
      const productType = String(order?.productType || '').toLowerCase();
      const orderStatus = String(order?.orderStatus || '').toLowerCase();
      return productType === 'kooul' && orderStatus === 'pending';
    });
  }, [orders]);

  useEffect(() => {
    const audio = kooulPendingAudioRef.current;
    if (!audio) {
      return;
    }

    if (hasKooulPendingOrders) {
      const playPromise = audio.play();
      if (playPromise?.catch) {
        playPromise.catch((error) => {
          console.error('Unable to play Kooul pending notification (screen orders):', error);
        });
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [hasKooulPendingOrders]);

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      navigate('/screens/login');
    }
  }, [isAuthLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    loadOrders();
    intervalRef.current = setInterval(loadOrders, 10000);
    return () => clearInterval(intervalRef.current);
  }, [token]);

  // clock tick for countdowns
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Cleanup modal on unmount
  useEffect(() => {
    return () => {
      // Clear modal timeout
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current);
      }
      
      // Dispose modal instance
      if (imageModalRef.current) {
        try {
          imageModalRef.current.hide();
          imageModalRef.current.dispose();
        } catch (e) {
          // ignore
        }
        imageModalRef.current = null;
      }
      
      // Clean up any remaining backdrops
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach((backdrop) => backdrop.remove());
    };
  }, []);

  const safeParseDate = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch (e) {
      return null;
    }
  };

  const getOrderDeadline = (order) => {
    const accepted = order?.customer_data?.accepted_time || order?.customer_data?.courier?.accepted_time || null;
    const committed = order?.customer_data?.committed_pickup_time || order?.customer_data?.committed_pickup_time || null;
    // if committed_pickup_time exists, use it as deadline
    const committedDate = safeParseDate(committed);
    if (committedDate) return committedDate;

    // fallback: createdAt + 8 minutes
    const created = order?.createdAt?.$date || order?.createdAt || null;
    const createdDate = safeParseDate(created);
    if (createdDate) {
      return new Date(createdDate.getTime() + 8 * 60 * 1000);
    }

    return null;
  };

  const formatCountdown = (ms) => {
    if (ms === null) return '—';
    const negative = ms < 0;
    const absMs = Math.abs(ms);
    const totalSeconds = Math.floor(absMs / 1000);
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${negative ? '-' : ''}${minutes}:${seconds}`;
  };

  const getCustomerPreference = (order) => {
    if (!order) return null;

    const directValue = order.customer_preference
      ?? order.customerPreference
      ?? order.customer_data?.customer_preference
      ?? order.customer_data?.customerPreference
      ?? order.meta_data?.customer_preference
      ?? order.meta_data?.customerPreference
      ?? order.meta_data?.woocommerce?.customer_preference
      ?? order.meta_data?.woocommerce?.customerPreference;

    if (directValue !== undefined && directValue !== null && String(directValue).trim() !== '') {
      return directValue;
    }

    const meta = Array.isArray(order.meta_data) ? order.meta_data : [];
    const preferenceMeta = meta.find((item) => {
      const key = String(item?.key || '').toLowerCase();
      return key === 'customer_preference' || key === '_customer_preference' || key === 'customer preference';
    });

    return preferenceMeta?.value ?? null;
  };

  const formatCustomerPreference = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) {
      return value.map((entry) => formatCustomerPreference(entry)).filter(Boolean).join('\n');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && String(entryValue).trim() !== '')
        .map(([key, entryValue]) => `${key}: ${typeof entryValue === 'object' ? JSON.stringify(entryValue) : entryValue}`)
        .join('\n');
    }
    return String(value).trim();
  };

  const renderCustomerPreferenceFlag = (order) => {
    const customerPreference = formatCustomerPreference(getCustomerPreference(order));
    if (!customerPreference) return null;

    return (
      <span
        className="badge bg-danger ms-2 align-middle"
        title={customerPreference}
        style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
      >
        <i className="fas fa-exclamation-triangle me-1"></i>
        Demande Client
      </span>
    );
  };

  const normalizeQuantityValue = (value) => {
    const parsed = parseInt(String(value ?? '').replace('x', '').trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  };

  const findMetaValue = (meta, keys = []) => {
    if (!Array.isArray(meta)) return '';
    const normalizedKeys = keys.map((key) => String(key).toLowerCase());
    const found = meta.find((item) => normalizedKeys.includes(String(item?.key || '').toLowerCase()));
    return found?.value ? String(found.value).trim() : '';
  };

  const getProductOrOrderNote = (order, product) => {
    const directCandidates = [
      product?.note,
      product?.mot,
      product?.message,
      product?.comment,
      product?.customer_note,
      product?.customerNote,
      product?.gift_message,
      product?.giftMessage,
      order?.note,
      order?.customer_note,
      order?.customerNote,
      order?.customer_data?.note,
      order?.customer_data?.customer_note,
      order?.customer_data?.customerNote,
      order?.meta_data?.note,
      order?.meta_data?.customer_note,
      order?.meta_data?.gift_message,
      order?.meta_data?.woocommerce?.customer_note,
    ];

    const direct = directCandidates.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
    if (direct) return String(direct).trim();

    return findMetaValue(order?.meta_data, [
      'Mot',
      'Mot d’accompagnement',
      'Mot d accompagnement',
      'Message',
      'message',
      'customer_note',
      '_customer_note',
      'gift_message',
      '_gift_message',
    ]);
  };

  const renderQuantityBadge = (quantity) => {
    const normalizedQuantity = normalizeQuantityValue(quantity);
    if (normalizedQuantity <= 1) return null;
    return (
      <span
        className="badge bg-danger me-2"
        style={{ fontSize: '1rem', boxShadow: '0 2px 8px rgba(220,53,69,0.35)' }}
      >
        {normalizedQuantity}x
      </span>
    );
  };

  const renderProductNoteIcon = (order, product, extraClassName = '') => {
    const note = getProductOrOrderNote(order, product);
    if (!note) return null;
    return (
      <span
        className={`badge bg-warning text-dark rounded-circle d-inline-flex align-items-center justify-content-center ${extraClassName}`}
        title={note}
        style={{
          width: 34,
          height: 34,
          fontSize: '1rem',
          boxShadow: '0 3px 10px rgba(0,0,0,0.22)',
        }}
      >
        <i className="fas fa-comment-dots"></i>
      </span>
    );
  };

  const getProductTypeTheme = (order) => {
    const productType = String(order?.productType || '').toLowerCase();
    if (productType === 'kooul') return { color: '#8B0000', textColor: '#ffffff' };
    if (productType === 'site') return { color: '#0dcaf0', textColor: '#17202a' };
    if (productType === 'glovo') return { color: '#ffc107', textColor: '#17202a' };
    if (productType === 'glovo_sisters') return { color: '#ffb585', textColor: '#17202a' };
    return { color: '#6c757d', textColor: '#ffffff' };
  };

  const getOrderLinkColor = (order) => {
    const key = String(order?.orderCode || order?._id || order?.orderNumber || '').trim();
    if (orderLinkColorsRef.current.has(key)) return orderLinkColorsRef.current.get(key);

    const hue = (265 + orderLinkColorIndexRef.current * 137.508) % 360;
    const color = `hsl(${hue.toFixed(1)} 78% 47%)`;
    orderLinkColorIndexRef.current += 1;
    orderLinkColorsRef.current.set(key, color);
    return color;
  };

  const shouldDetachProductDetails = (product) => {
    const title = String(product?.title || product?.name || '').trim();
    const optionsCount = Array.isArray(product?.options) ? product.options.length : 0;
    return optionsCount >= 4 || title.length > 50;
  };

  const getLinkedProductProps = (order, productIndex, productsCount) => {
    const isMultiProductOrder = productsCount > 1;
    const color = getOrderLinkColor(order);
    return {
      className: isMultiProductOrder ? 'screen-order-linked-product' : '',
      style: isMultiProductOrder
        ? {
            '--screen-order-link-color': color,
          }
        : {},
      showConnector: isMultiProductOrder && productIndex < productsCount - 1,
    };
  };

  const filteredOrders = useMemo(() => {
    return Array.isArray(orders) ? orders.filter((o) => Array.isArray(o.products) && o.products.length) : [];
  }, [orders]);

  // Auto-assign distinct users to columns when orders change
  useEffect(() => {
    if (!filteredOrders || filteredOrders.length === 0) return;
    
    const distinctUserIds = new Set();
    filteredOrders.forEach((order) => {
      const v = order?.affected?.user_id;
      const uid = !v ? null : (typeof v === 'object' ? (v._id || v) : v);
      if (uid) distinctUserIds.add(String(uid));
    });
    
    const userIdsArray = Array.from(distinctUserIds);
    if (userIdsArray.length > 0) {
      setColumnUserIds((prev) => {
        const hasCustom = sessionStorage.getItem(getColumnUsersKey());
        if (hasCustom) return prev; // Keep user's custom selection
        
        const out = Array(NUM_COLUMNS).fill(null);
        for (let i = 0; i < NUM_COLUMNS && i < userIdsArray.length; i++) {
          out[i] = userIdsArray[i];
        }
        return out;
      });
    }
  }, [filteredOrders]);


  useEffect(() => {
    const fetchStoreNames = async () => {
      try {
        const resp = await axios.get(`${API_URL}/distinctStoreIds`, header());
        const list = resp.data?.response || [];
        const session = getSessionScreenAuth();
        const ids = session?.store_ids || [];
        if (!ids || ids.length === 0) {
          setStoreNames([]);
          return;
        }

        const names = ids.map((id) => {
          const found = list.find((s) => String(s._id) === String(id) || String(s.storeCode) === String(id) || String(s.code) === String(id));
          return found ? (found.name || found.storeName || found.storeCode) : String(id);
        });
        setStoreNames(names.filter(Boolean));

        // Fetch all users
        try {
          const userResp = await axios.get(`${API_URL}/users`, header());
          const users = userResp.data || [];
          const usersMap = {};
          const userIds = [];
          users.forEach((user) => {
            const fullName = [user.name, user.lastname].filter(Boolean).join(' ') || user.username || 'User';
            usersMap[user._id] = fullName;
            userIds.push(user._id);
          });
          setStoreUsers(usersMap);
          setUsersList(userIds);
          // if columnUserIds are empty, initialize with nulls (shows all orders)
          setColumnUserIds((prev) => {
            const any = (prev || []).some((v) => v !== null && v !== undefined);
            if (any) return prev;
            // Start with null to show all orders, user can then select specific users
            const out = Array(NUM_COLUMNS).fill(null);
            return out;
          });
        } catch (error) {
          console.error('Unable to load users:', error);
        }
      } catch (error) {
        console.error('Unable to load store names for screen orders', error);
      }
    };

    fetchStoreNames();
  }, []);

  if (isAuthLoading || isLoading) {
    return null;
  }

  if (!orders.length) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <h2 className="mb-3">Aucune commande pour le moment</h2>
          <p className="text-muted">Les commandes apparaîtront automatiquement dès qu'elles seront disponibles.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between p-2 bg-light w-100">
        <div className="d-flex align-items-center gap-3">
          <div className="fw-bold">Magasins: {storeNames && storeNames.length ? storeNames.join(', ') : '—'}</div>
          <div className="btn-group btn-group-sm" role="group">
            <button 
              type="button" 
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('grid')}
            >
              <i className="fas fa-th"></i> Grille
            </button>
            <button 
              type="button" 
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('list')}
            >
              <i className="fas fa-list"></i> Liste
            </button>
            <button 
              type="button" 
              className={`btn ${viewMode === 'user' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setViewMode('user')}
            >
              <i className="fas fa-user"></i> Utilisateurs
            </button>
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div className="btn-group btn-group-sm me-2" role="group">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={prevColumn}><i className="fas fa-arrow-left"></i></button>
            <button type="button" className="btn btn-light btn-sm" disabled>Col {userColumn}/{NUM_COLUMNS}</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={nextColumn}><i className="fas fa-arrow-right"></i></button>
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleDisconnect}>Déconnecter</button>
        </div>
      </div>

      {viewMode === 'grid' ? (
        <div className="parent screen-orders-grid d-flex flex-wrap">
          {orders.map((order) => {
            const productType = (order?.productType || '').toLowerCase();
            const isKooulOrder = productType === 'kooul';
            const isSiteOrder = productType === 'site';
            const isSistersOrder = productType === 'glovo_sisters';

            const products = Array.isArray(order?.products) ? order.products : [];
            if (!products.length) return null;

            const { color: productTypeColor, textColor: productTypeTextColor } = getProductTypeTheme(order);

            const orderLinkColor = getOrderLinkColor(order);
            const orderBorderColor = products.length > 1 ? orderLinkColor : '#ffffff';

            return products.map((product, index) => {
              const resolvedImageUrl = resolveImageUrl(product?.urlImage || product?.image);
              const quantityLabel = normalizeQuantityValue(product?.quantity);
              const productName = product?.title || product?.name || '';
              const hasNextProduct = index < products.length - 1;
              const detachDetails = shouldDetachProductDetails(product);
              const showNextProductLink = hasNextProduct;
              const sharedCardStyle = {
                '--screen-order-link-color': orderLinkColor,
                position: 'relative',
                border: `4px solid ${orderBorderColor}`,
                borderRadius: '8px',
                overflow: 'visible',
              };

              if (!detachDetails) {
                return (
                  <div
                    key={`${order.orderCode || order._id}-${index}`}
                    className={`grid-item screen-order-combined-card ${showNextProductLink ? 'screen-order-linked-product' : ''} ${isKooulOrder ? 'grid-item--kooul' : ''} ${isSiteOrder ? 'grid-item--site' : ''} ${isSistersOrder ? 'grid-item--glovo-sisters' : ''}`}
                    style={sharedCardStyle}
                  >
                    {showNextProductLink && (
                      <div className="screen-order-link-chip" title="Produit suivant de la même commande">
                        <i className="fas fa-link"></i>
                      </div>
                    )}
                    <div className="flex">
                      <div
                        className="top-row bg background-image-div"
                        style={{
                          backgroundImage: resolvedImageUrl ? `url('${resolvedImageUrl}')` : undefined,
                          backgroundColor: productTypeColor,
                          cursor: resolvedImageUrl ? 'pointer' : 'default',
                          position: 'relative',
                        }}
                        onClick={() => openImage(product?.urlImage)}
                      >
                        {renderProductNoteIcon(order, product, 'position-absolute top-0 start-0 m-2')}
                        {(() => {
                          const deadline = getOrderDeadline(order);
                          if (!deadline) return null;
                          const remainingMs = deadline.getTime() - nowTick;
                          return (
                            <div style={{ position: 'absolute', top: 8, right: 8 }}>
                              <div style={{
                                background: remainingMs < 0 ? '#dc3545' : '#007bff',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: 6,
                                fontWeight: 700,
                                fontSize: 12,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}>
                                {formatCountdown(remainingMs)}
                              </div>
                            </div>
                          );
                        })()}
                        <span className="orderNumber" style={{ backgroundColor: productTypeColor, color: productTypeTextColor }}>
                          {order.orderNumber || order.orderCode || 'Commande'}
                          {(order?.affected?.name || order?.affected?.user_id) && (
                            <span className="badge bg-white text-dark ms-2" style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', border: '1px solid #ddd' }}>
                              <i className="fas fa-user me-1"></i>
                              {order?.affected?.name || 'Affecté'}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="bottom-row row screen-order-combined-details" style={{ backgroundColor: productTypeColor, color: productTypeTextColor }}>
                        <div className="col-8">
                          <h3 className="title-under screen-order-combined-title">
                            {renderQuantityBadge(quantityLabel)}
                            {productName || 'Produit'}
                            {renderCustomerPreferenceFlag(order)}
                          </h3>
                          {product.options?.length > 0 && product.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="title-under screen-order-combined-option">
                              {option.quantity || 1}x {option.title}
                            </div>
                          ))}
                        </div>
                        <div className="col-4 d-flex align-items-end justify-content-end">
                          {productType !== 'glovo' && (
                            <button className="par-button screen-order-combined-end" onClick={() => endOrder(order.orderCode)}>End</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="screen-order-detached-pair" key={`${order.orderCode || order._id}-${index}`}>
                  <div
                    className={`grid-item screen-order-linked-product screen-order-image-card ${isKooulOrder ? 'grid-item--kooul' : ''} ${isSiteOrder ? 'grid-item--site' : ''} ${isSistersOrder ? 'grid-item--glovo-sisters' : ''}`}
                    style={sharedCardStyle}
                  >
                    <div className="screen-order-link-chip" title="Voir les informations du produit">
                      <i className="fas fa-arrow-right"></i>
                    </div>
                    <div className="flex screen-order-image-flex">
                      <div
                        className="top-row bg background-image-div"
                        style={{
                          backgroundImage: resolvedImageUrl ? `url('${resolvedImageUrl}')` : undefined,
                          backgroundColor: productTypeColor,
                          cursor: resolvedImageUrl ? 'pointer' : 'default',
                          position: 'relative',
                        }}
                        onClick={() => openImage(product?.urlImage)}
                      >
                        {renderProductNoteIcon(order, product, 'position-absolute top-0 start-0 m-2')}
                        {(() => {
                          const deadline = getOrderDeadline(order);
                          if (!deadline) return null;
                          const remainingMs = deadline.getTime() - nowTick;
                          const formatted = formatCountdown(remainingMs);
                          const isOverdue = remainingMs < 0;
                          return (
                            <div style={{ position: 'absolute', top: 8, right: 8 }}>
                              <div style={{
                                background: isOverdue ? '#dc3545' : '#007bff',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: 6,
                                fontWeight: 700,
                                fontSize: 12,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}>
                                {formatted}
                              </div>
                            </div>
                          );
                        })()}
                        <span className="orderNumber" style={{ backgroundColor: productTypeColor, color: productTypeTextColor }}>
                          {order.orderNumber || order.orderCode || 'Commande'}
                          {(order?.affected?.name || order?.affected?.user_id) && (
                            <span className="badge bg-white text-dark ms-2" style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem', border: '1px solid #ddd' }}>
                              <i className="fas fa-user me-1"></i>
                              {order?.affected?.name ? order.affected.name : (() => {
                                const userId = typeof order.affected.user_id === 'object' ? order.affected.user_id?._id : order.affected.user_id;
                                return storeUsers[userId] || (typeof order.affected.user_id === 'object'
                                  ? [order.affected.user_id?.name, order.affected.user_id?.lastname].filter(Boolean).join(' ') || order.affected.user_id?.email || order.affected.user_id?.username
                                  : userId) || 'Affecté';
                              })()}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`grid-item screen-order-details-card ${showNextProductLink ? 'screen-order-linked-product' : ''}`}
                    style={sharedCardStyle}
                  >
                    {showNextProductLink && (
                      <div className="screen-order-link-chip" title="Produit suivant de la même commande">
                        <i className="fas fa-link"></i>
                      </div>
                    )}
                    <div className="screen-order-details-panel" style={{ backgroundColor: productTypeColor, color: productTypeTextColor }}>
                      <div className="screen-order-details-content">
                        <h3 className="screen-order-details-title">
                          {renderQuantityBadge(quantityLabel)}
                          {productName || 'Produit'}
                          {renderCustomerPreferenceFlag(order)}
                        </h3>
                        {product.options?.length > 0 && product.options.map((option, idx) => (
                          <div key={idx} className="screen-order-details-option">
                            {option.quantity || 1}x {option.title}
                          </div>
                        ))}
                      </div>
                      {productType !== 'glovo' && (
                        <button className="par-button screen-order-details-end" onClick={() => endOrder(order.orderCode)}>End</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            });
          })}
        </div>
      ) : viewMode === 'list' ? (
        <div className="container-fluid p-3">
          <div className="row g-3">
            {filteredOrders.map((order, index) => {
              const productType = (order?.productType || '').toLowerCase();
              const isKooulOrder = productType === 'kooul';
              const isSiteOrder = productType === 'site';
              const isSistersOrder = productType === 'glovo_sisters';
              const products = Array.isArray(order?.products) ? order.products : [];
              if (!products.length) return null;

              const colIndex = (index % NUM_COLUMNS) + 1;
              const isUserCol = colIndex === userColumn;

              const deadline = getOrderDeadline(order);
              const remainingMs = deadline ? deadline.getTime() - nowTick : null;
              const formatted = formatCountdown(remainingMs);
              const isOverdue = remainingMs < 0;

              let bgColorClass = 'bg-secondary';
              let borderColorClass = 'border-secondary';
              let bodyBgColor = '#f8f9fa';
              let headerInlineStyle = null;
              let footerInlineStyle = null;
              let cardBorderInline = null;

              if (isKooulOrder) {
                bgColorClass = '';
                borderColorClass = '';
                headerInlineStyle = { background: '#8B0000', color: '#fff' };
                footerInlineStyle = { background: '#8B0000' };
                cardBorderInline = { borderColor: '#8B0000' };
                bodyBgColor = '#ffebee'; // Light red
              } else if (isSiteOrder) {
                bgColorClass = 'bg-info';
                borderColorClass = 'border-info';
                bodyBgColor = '#cfe2ff'; // Light blue
              } else if (productType === 'glovo') {
                bgColorClass = 'bg-warning';
                borderColorClass = 'border-warning';
                bodyBgColor = '#fff3cd'; // Light yellow
              } else if (isSistersOrder) {
                const sistersColor = '#ffb585';
                const sistersBodyBg = '#fff4e6';
                bgColorClass = '';
                borderColorClass = '';
                headerInlineStyle = { background: sistersColor, color: '#000' };
                footerInlineStyle = { background: sistersColor };
                cardBorderInline = { borderColor: sistersColor };
                bodyBgColor = sistersBodyBg;
              }

              return (
                <div key={order.orderCode || order._id} className={`col-lg-3 col-md-4 col-sm-6 d-flex ${isUserCol ? 'user-column--selected' : ''}`}>
                  <div className={`card h-100 shadow-sm w-100 border-3 ${borderColorClass}`} style={{ borderWidth: '3px', ...(cardBorderInline || {}), ...(isUserCol ? { boxShadow: '0 0 0 4px rgba(0,123,255,0.18)' } : {}) }}>
                    <div className={`card-header ${isSistersOrder ? '' : `${bgColorClass} text-white`} d-flex justify-content-between align-items-center py-3`} style={headerInlineStyle || undefined}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold" style={{ fontSize: '1.5rem' }}>{order.orderNumber || order.orderCode || 'Commande'}</span>
                        {(order?.affected?.name || order?.affected?.user_id) && (
                          <span className="badge bg-white text-dark" style={{ fontSize: '0.9rem', padding: '0.4rem 0.7rem' }}>
                            <i className="fas fa-user me-1"></i>
                            {order?.affected?.name ? (
                              order.affected.name
                            ) : (
                              (() => {
                                const userId = typeof order.affected.user_id === 'object' ? order.affected.user_id?._id : order.affected.user_id;
                                return storeUsers[userId] || 
                                  (typeof order.affected.user_id === 'object'
                                    ? [order.affected.user_id?.name, order.affected.user_id?.lastname].filter(Boolean).join(' ') || order.affected.user_id?.email || order.affected.user_id?.username
                                    : userId) || 'Affecté';
                              })()
                            )}
                          </span>
                        )}
                      </div>
                      {deadline && (
                        <span className={`badge ${isOverdue ? 'bg-danger' : 'bg-primary'}`} style={{ fontSize: '1rem', padding: '0.5rem 0.75rem' }}>
                          {formatted}
                        </span>
                      )}
                    </div>
                    <div className="card-body p-3" style={{ maxHeight: '500px', overflowY: 'auto', backgroundColor: bodyBgColor }}>
                      {products.map((product, index) => {
                        const resolvedImageUrl = resolveImageUrl(product?.urlImage || product?.image);
                        const quantityLabel = normalizeQuantityValue(product?.quantity);
                        const productName = product?.title || product?.name || '';
                        const linkProps = getLinkedProductProps(order, index, products.length);

                        return (
                          <div
                            key={index}
                            className={`screen-order-product-row ${linkProps.className} d-flex gap-3 mb-3 pb-3 border-bottom`}
                            style={linkProps.style}
                          >
                            {linkProps.showConnector && (
                              <div className="screen-order-link-chip screen-order-link-chip--row" title="Produit lié à la même commande">
                                <i className="fas fa-link"></i>
                              </div>
                            )}
                            <div 
                              style={{ 
                                width: '80px', 
                                height: '80px', 
                                flexShrink: 0,
                                cursor: resolvedImageUrl ? 'pointer' : 'default',
                                position: 'relative'
                              }}
                              onClick={() => openImage(product?.urlImage)}
                            >
                              {renderProductNoteIcon(order, product, 'position-absolute top-0 start-0 m-1')}
                              {resolvedImageUrl ? (
                                <img 
                                  src={resolvedImageUrl} 
                                  alt={productName}
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover',
                                    borderRadius: '6px'
                                  }}
                                />
                              ) : (
                                <div style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  background: '#e9ecef',
                                  borderRadius: '6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <i className="fas fa-image text-muted" style={{ fontSize: '1.5rem' }}></i>
                                </div>
                              )}
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: '1.3' }}>
                                {renderQuantityBadge(quantityLabel)}
                                {productName}
                                {renderCustomerPreferenceFlag(order)}
                              </div>
                              {product.options?.length > 0 && (
                                <div className="mt-2" style={{ paddingLeft: '1.5rem' }}>
                                  {product.options.map((option, idx) => (
                                    <div key={idx} className="mb-1" style={{ fontSize: '1rem' }}>
                                      <span className="badge bg-dark me-2" style={{ fontSize: '0.9rem' }}>
                                        {option.quantity || 1}x
                                      </span>
                                      {option.title}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className={`card-footer ${isSistersOrder ? '' : bgColorClass} d-flex justify-content-end align-items-center py-2`} style={footerInlineStyle || undefined}>
                      {productType !== "glovo" && (
                        <button 
                          className="btn btn-sm btn-danger fw-bold" 
                          style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}
                          onClick={() => endOrder(order.orderCode)}
                        >
                          End
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="container-fluid p-3">
          <div className="row g-3">
            {Array.from({ length: NUM_COLUMNS }).map((_, colIdx) => {
              const userId = columnUserIds[colIdx];
              
              // Always render the column, even if no user is assigned
              const ordersForUser = userId ? filteredOrders.filter((o) => {
                const v = o?.affected?.user_id;
                const uid = !v ? null : (typeof v === 'object' ? (v._id || v) : v);
                return uid && String(uid) === String(userId);
              }) : [];

              // Get user name from first order's affected.name
              const firstOrder = ordersForUser[0];
              const userName = userId ? (firstOrder?.affected?.name || storeUsers[userId] || String(userId)) : `Colonne ${colIdx + 1}`;

              // Calculate total products across all orders for this user
              const totalProducts = ordersForUser.reduce((sum, order) => {
                const products = Array.isArray(order?.products) ? order.products : [];
                return sum + products.reduce((pSum, product) => {
                  const qty = parseInt(product?.quantity?.toString().replace('x', '') || '1', 10);
                  return pSum + (Number.isFinite(qty) ? qty : 1);
                }, 0);
              }, 0);
              
              // Check if this column is the currently selected one
              const isSelectedColumn = (colIdx + 1) === userColumn;

              return (
                <div key={`user-col-${colIdx}`} className="col-lg-3 col-md-6 col-sm-12">
                  <div 
                    className="w-100 p-3 rounded" 
                    style={{
                      backgroundColor: isSelectedColumn ? 'rgba(0, 123, 255, 0.05)' : 'transparent',
                      border: isSelectedColumn ? '2px solid rgba(0, 123, 255, 0.3)' : '2px solid transparent',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => prevUserForColumn(colIdx)}><i className="fas fa-arrow-left"></i></button>
                        <button type="button" className="btn btn-light btn-sm" disabled>{userName}</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => nextUserForColumn(colIdx)}><i className="fas fa-arrow-right"></i></button>
                      </div>
                      <div className="d-flex gap-2">
                        {userId && (
                          <>
                            <div className="badge bg-secondary" style={{ fontSize: '1.1rem', padding: '0.5rem 0.8rem' }}>
                              {ordersForUser.length} commande{ordersForUser.length > 1 ? 's' : ''}
                            </div>
                            <div className="badge bg-primary" style={{ fontSize: '1.1rem', padding: '0.5rem 0.8rem' }}>
                              <i className="fas fa-shopping-basket me-1"></i>{totalProducts} produit{totalProducts > 1 ? 's' : ''}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {!userId ? (
                      <div className="text-center p-4 border border-2 border-dashed rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <i className="fas fa-user-slash text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                        <div className="text-muted">Aucun utilisateur assigné</div>
                        <small className="text-muted">Utilisez les flèches pour sélectionner un utilisateur</small>
                      </div>
                    ) : ordersForUser.length === 0 ? (
                      <div className="text-center p-4 border border-2 border-dashed rounded" style={{ backgroundColor: '#f8f9fa' }}>
                        <i className="fas fa-inbox text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                        <div className="text-muted">Aucune commande pour cet utilisateur</div>
                      </div>
                    ) : (
                      ordersForUser.map((order) => {
                        const products = Array.isArray(order?.products) ? order.products : [];
                        const productType = (order?.productType || '').toLowerCase();
                        const isKooulOrder = productType === 'kooul';
                        const isSiteOrder = productType === 'site';
                        const isSistersOrder = productType === 'glovo_sisters';
                        const deadline = getOrderDeadline(order);
                        const remainingMs = deadline ? deadline.getTime() - nowTick : null;
                        const formatted = formatCountdown(remainingMs);
                        const isOverdue = remainingMs < 0;

                        let bgColorClass = 'bg-secondary';
                        let borderColorClass = 'border-secondary';
                        let bodyBgColor = '#f8f9fa';
                        let headerInlineStyle = null;
                        let footerInlineStyle = null;
                        let cardBorderInline = null;

                        if (isKooulOrder) {
                          bgColorClass = '';
                          borderColorClass = '';
                          headerInlineStyle = { background: '#8B0000', color: '#fff' };
                          footerInlineStyle = { background: '#8B0000' };
                          cardBorderInline = { borderColor: '#8B0000' };
                          bodyBgColor = '#ffebee'; // Light red
                        } else if (isSiteOrder) {
                          bgColorClass = 'bg-info';
                          borderColorClass = 'border-info';
                          bodyBgColor = '#cfe2ff'; // Light blue
                        } else if (productType === 'glovo') {
                          bgColorClass = 'bg-warning';
                          borderColorClass = 'border-warning';
                          bodyBgColor = '#fff3cd'; // Light yellow
                        } else if (isSistersOrder) {
                          const sistersColor = '#ffb585';
                          const sistersBodyBg = '#fff4e6';
                          bgColorClass = '';
                          borderColorClass = '';
                          headerInlineStyle = { background: sistersColor, color: '#000' };
                          footerInlineStyle = { background: sistersColor };
                          cardBorderInline = { borderColor: sistersColor };
                          bodyBgColor = sistersBodyBg;
                        }

                        return (
                          <div key={order.orderCode || order._id} className={`card shadow-sm w-100 border-3 mb-3 ${borderColorClass}`} style={{ borderWidth: '3px', ...(cardBorderInline || {}) }}>
                            <div className={`card-header ${isSistersOrder ? '' : `${bgColorClass} text-white`} d-flex justify-content-between align-items-center py-3`} style={headerInlineStyle || undefined}>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold" style={{ fontSize: '1.5rem' }}>{order.orderNumber || order.orderCode || 'Commande'}</span>
                                {(order?.affected?.name || order?.affected?.user_id) && (
                                  <span className="badge bg-white text-dark" style={{ fontSize: '0.9rem', padding: '0.4rem 0.7rem' }}>
                                    <i className="fas fa-user me-1"></i>
                                    {order?.affected?.name ? (
                                      order.affected.name
                                    ) : (
                                      (() => {
                                        const userId = typeof order.affected.user_id === 'object' ? order.affected.user_id?._id : order.affected.user_id;
                                        return storeUsers[userId] || 
                                          (typeof order.affected.user_id === 'object'
                                            ? [order.affected.user_id?.name, order.affected.user_id?.lastname].filter(Boolean).join(' ') || order.affected.user_id?.email || order.affected.user_id?.username
                                            : userId) || 'Affecté';
                                      })()
                                    )}
                                  </span>
                                )}
                              </div>
                              {deadline && (
                                <span className={`badge ${isOverdue ? 'bg-danger' : 'bg-primary'}`} style={{ fontSize: '1rem', padding: '0.5rem 0.75rem' }}>
                                  {formatted}
                                </span>
                              )}
                            </div>
                            <div className="card-body p-3" style={{ maxHeight: '500px', overflowY: 'auto', backgroundColor: bodyBgColor }}>
                              {products.map((product, idx2) => {
                                const resolvedImageUrl = resolveImageUrl(product?.urlImage || product?.image);
                                const quantityLabel = normalizeQuantityValue(product?.quantity);
                                const productName = product?.title || product?.name || '';
                                const linkProps = getLinkedProductProps(order, idx2, products.length);

                                return (
                                  <div
                                    key={idx2}
                                    className={`screen-order-product-row ${linkProps.className} d-flex gap-3 mb-3 pb-3 border-bottom`}
                                    style={linkProps.style}
                                  >
                                    {linkProps.showConnector && (
                                      <div className="screen-order-link-chip screen-order-link-chip--row" title="Produit lié à la même commande">
                                        <i className="fas fa-link"></i>
                                      </div>
                                    )}
                                    <div 
                                      style={{ 
                                        width: '80px', 
                                        height: '80px', 
                                        flexShrink: 0,
                                        cursor: resolvedImageUrl ? 'pointer' : 'default',
                                        position: 'relative'
                                      }}
                                      onClick={() => openImage(product?.urlImage)}
                                    >
                                      {renderProductNoteIcon(order, product, 'position-absolute top-0 start-0 m-1')}
                                      {resolvedImageUrl ? (
                                        <img 
                                          src={resolvedImageUrl} 
                                          alt={productName}
                                          style={{ 
                                            width: '100%', 
                                            height: '100%', 
                                            objectFit: 'cover',
                                            borderRadius: '6px'
                                          }}
                                        />
                                      ) : (
                                        <div style={{ 
                                          width: '100%', 
                                          height: '100%', 
                                          background: '#e9ecef',
                                          borderRadius: '6px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}>
                                          <i className="fas fa-image text-muted" style={{ fontSize: '1.5rem' }}></i>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <div className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: '1.3' }}>
                                        {renderQuantityBadge(quantityLabel)}
                                        {productName}
                                        {renderCustomerPreferenceFlag(order)}
                                      </div>
                                      {product.options?.length > 0 && (
                                        <div className="mt-2" style={{ paddingLeft: '1.5rem' }}>
                                          {product.options.map((option, idx3) => (
                                            <div key={idx3} className="mb-1" style={{ fontSize: '1rem' }}>
                                              <span className="badge bg-dark me-2" style={{ fontSize: '0.9rem' }}>
                                                {option.quantity || 1}x
                                              </span>
                                              {option.title}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className={`card-footer ${isSistersOrder ? '' : bgColorClass} d-flex justify-content-end align-items-center py-2`} style={footerInlineStyle || undefined}>
                              {productType !== 'glovo' && (
                                <button 
                                  className="btn btn-sm btn-danger fw-bold" 
                                  style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}
                                  onClick={() => endOrder(order.orderCode)}
                                >
                                  End
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal
        modalId="showModal"
        modalType="modal-xl"
        title="Aperçu image"
        onClose={handleImageModalHidden}
        hideFooter
        headerActions={(
          <div className="screen-order-image-toolbar" role="toolbar" aria-label="Contrôles du zoom">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setImageZoom((value) => Math.max(0.5, Number((value - 0.25).toFixed(2))))}
              disabled={imageZoom <= 0.5}
              aria-label="Zoom arrière"
              title="Zoom arrière"
            >
              <i className="fas fa-search-minus"></i>
            </button>
            <button type="button" className="btn btn-light screen-order-image-zoom-value" onClick={() => setImageZoom(1)} aria-label="Réinitialiser le zoom" title="Réinitialiser le zoom">
              {Math.round(imageZoom * 100)}%
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setImageZoom((value) => Math.min(3, Number((value + 0.25).toFixed(2))))}
              disabled={imageZoom >= 3}
              aria-label="Zoom avant"
              title="Zoom avant"
            >
              <i className="fas fa-search-plus"></i>
            </button>
          </div>
        )}
      >
        <div className="screen-order-image-modal">
          <div className="screen-order-image-modal-viewport">
          {modalImage ? (
              <img
                src={modalImage}
                alt="Aperçu du produit"
                className="screen-order-modal-image"
                style={imageZoom <= 1
                  ? { maxWidth: `${imageZoom * 100}%`, maxHeight: `${74 * imageZoom}vh` }
                  : { width: `${imageZoom * 100}%`, maxWidth: 'none', maxHeight: 'none' }}
              />
          ) : (
            <p className="text-muted">Aucune image disponible</p>
          )}
          </div>
        </div>
      </Modal>

      <Modal modalId="detailsComponent" title={`Composant du produit ${currentProduct?.name}`} modalType="modal-lg">
        <div>
          {!currentProduct ? (
            <p className="text-muted">Veuillez patienter...</p>
          ) : Array.isArray(currentProduct.components) && currentProduct.components.length > 0 ? (
            <ul className="list-group">
              {currentProduct.components.map((component, index) => {
                const reference = component.product ?? component.component;
                const componentName = component.name || (typeof reference === 'object' ? reference?.name : '') || 'Composant inconnu';
                const quantityDisplay = component.quantity ?? 0;
                return (
                  <li key={index} className="list-group-item h2 text-black">
                    {quantityDisplay}x {componentName}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default ScreenOrdersView;
